import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { FormChangeTracker } from 'src/app/shared/utils/form-change-tracker.util';
import { GREETING_CONFIG_MAX_LENGTH, parseGreetingConfig, stringifyGreetingConfig } from 'src/app/shared/utils/greeting-config.util';
import { ConstantUtil } from 'src/app/util/constant.util';

import {
  parseWorkTimeRanges,
  serializeWorkTimeRanges,
  validateWorkTimeRanges,
  type WorkTimeRange,
  type WorkTimeValidationError
} from './work-time-range.util';

type ServiceConfigOption = {
  value: number;
  labelKey?: string;
  labelText?: string;
  transient?: boolean;
};

@Component({
  selector: 'servicenumber-config',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    I18nPipe,
    NzFormModule,
    NzInputModule,
    NzInputNumberModule,
    NzCardModule,
    NzDividerModule,
    NzGridModule,
    NzIconModule,
    NzSwitchModule,
    NzToolTipModule,
    NzSelectModule,
    NzButtonModule,
    NzDatePickerModule,
    NzModalModule,
    TestIdDirective
  ],
  templateUrl: './servicenumber-config.component.html',
  styleUrls: ['./servicenumber-config.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServiceConfigComponent implements OnInit, OnDestroy {
  private readonly http = inject(HttpService);
  private readonly fb = inject(FormBuilder);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private formChangeTracker?: FormChangeTracker;

  @Input() serviceNumberId: string = '';
  @Input() serviceNumberType: string = ''; // 新增：接收服务号类型
  @Input() tenantType: string = ''; // 新增：接收租户类型
  @Input() embeddedInDetail: boolean = false;
  @Output() readonly unsavedChangesChange = new EventEmitter<boolean>();
  isSaving = false;
  serviceNumberConfigForm!: FormGroup;
  workTimeRanges!: FormArray<FormGroup>;
  workTimeModalVisible = false;
  loading = false;
  hasUnsavedChanges = false;
  workTimeValidationError: WorkTimeValidationError | null = null;

  readonly workTimeHourOptions = Array.from({ length: 24 }, (_, value) => ({ value, label: this.formatTimeOption(value) }));
  readonly workTimeEndHourOptions = [...this.workTimeHourOptions, { value: 24, label: '24' }];
  readonly workTimeMinuteOptions = Array.from({ length: 60 }, (_, value) => ({ value, label: this.formatTimeOption(value) }));

  private readonly baseTimeoutOptions: ServiceConfigOption[] = [
    { value: 30, labelKey: 'servicenumber.config.timeout-option.30-minutes' },
    { value: 60, labelKey: 'servicenumber.config.timeout-option.1-hour' },
    { value: 360, labelKey: 'servicenumber.config.timeout-option.6-hours' },
    { value: 720, labelKey: 'servicenumber.config.timeout-option.12-hours' },
    { value: 1440, labelKey: 'servicenumber.config.timeout-option.24-hours' }
  ];
  private readonly baseIdleTimeOptions: ServiceConfigOption[] = [
    { value: 300, labelKey: 'servicenumber.config.idle-option.5-minutes' },
    { value: 600, labelKey: 'servicenumber.config.idle-option.10-minutes' },
    { value: 900, labelKey: 'servicenumber.config.idle-option.15-minutes' },
    { value: 1800, labelKey: 'servicenumber.config.idle-option.30-minutes' },
    { value: 3600, labelKey: 'servicenumber.config.idle-option.1-hour' }
  ];
  // 添加下拉选项数组
  timeoutOptions: ServiceConfigOption[] = [...this.baseTimeoutOptions];
  idleTimeOptions: ServiceConfigOption[] = [...this.baseIdleTimeOptions];
  // 新增：满意度相关选项
  surveyTypeOptions: Array<{ label: string; description: string; value: string }> = [
    {
      label: 'servicenumber.config.survey-type.simple',
      description: 'servicenumber.config.survey-type.simple.desc',
      value: 'Default'
    },
    {
      label: 'servicenumber.config.survey-type.complete',
      description: 'servicenumber.config.survey-type.complete.desc',
      value: 'Notice'
    }
  ];
  noticeTypeOptions: Array<{ label: string; value: string }> = [
    { label: 'servicenumber.config.notice-type.email', value: 'Email' },
    { label: 'servicenumber.config.notice-type.sms', value: 'Sms' }
  ];
  robotServiceType: string | null = null;
  readonly greetingQuestionMaxLength = GREETING_CONFIG_MAX_LENGTH;

  ngOnInit(): void {
    this.initForm();
    this.setupFormChangeDetection();

    if (this.serviceNumberId) {
      this.loadConfigData();
    }
  }

  ngOnDestroy(): void {
    this.formChangeTracker?.stop();
  }

  get selectedSurveyTypeDescription(): string | null {
    const selectedSurveyType = this.serviceNumberConfigForm?.get('surveyType')?.value;
    return this.surveyTypeOptions.find(option => option.value === selectedSurveyType)?.description ?? null;
  }

  // 初始化表单
  initForm(): void {
    this.workTimeRanges = this.fb.array<FormGroup>([this.createWorkTimeRangeGroup()]);
    this.serviceNumberConfigForm = this.fb.group({
      serviceNumberId: [this.serviceNumberId],
      workTime: [null, [Validators.required]],
      nonWorkTimeGreetings: [null, [Validators.required]],
      firstGreetings: [null],
      everyGreetings: [null],
      robotServiceType: [null],
      useRobot: [false],
      aiServiceStatus: [null],
      useRobotPriorityService: [false],
      useGreetingQuestion: [false],
      greetingQuestionText: [null, [Validators.maxLength(GREETING_CONFIG_MAX_LENGTH)]],
      useHandOverTextReply: [false],
      handOverTextReplyText: [null],
      idleTime: [300, [Validators.required]],
      idleGreetings: [null],
      timeoutTime: [30, [Validators.required]],
      expireTime: [null, [this.expireTimeFutureValidator]],
      ownerAutoTransferTime: [null], // 新增字段，非必填
      useWebchat: [false],
      useBusinessCard: [false],
      useVoiceToText: [false],
      voiceToTextJson: [null],
      // 新增：满意度相关字段
      useSatisfactionSurvey: [false],
      surveyType: [null],
      surveyRate: [null],
      useSatisfactionSurveyNotice: [false],
      noticeType: [[]],
      noticeEmail: [null],
      noticeSms: [null]
    });

    // 监听 useVoiceToText 的变化，动态设置 voiceToTextJson 的验证规则
    this.serviceNumberConfigForm.get('useVoiceToText')?.valueChanges.subscribe(useVoice => {
      const voiceToTextJsonControl = this.serviceNumberConfigForm.get('voiceToTextJson');
      if (useVoice) {
        voiceToTextJsonControl?.setValidators([Validators.required]);
      } else {
        voiceToTextJsonControl?.clearValidators();
      }
      voiceToTextJsonControl?.updateValueAndValidity();
    });

    // 新增：满意度调查启用时的校验
    this.serviceNumberConfigForm.get('useSatisfactionSurvey')?.valueChanges.subscribe(() => {
      this.syncSatisfactionValidators();
    });

    // 新增：满意度通知启用时的校验
    this.serviceNumberConfigForm.get('useSatisfactionSurveyNotice')?.valueChanges.subscribe(() => {
      this.syncSatisfactionValidators();
    });

    // 新增：根据通知类型动态验证邮箱和短信
    this.serviceNumberConfigForm.get('noticeType')?.valueChanges.subscribe(() => {
      this.syncSatisfactionValidators();
    });

    this.serviceNumberConfigForm.get('workTime')?.valueChanges.subscribe(value => {
      this.syncWorkTimeRangesFromValue(value);
    });
    this.workTimeRanges.valueChanges.subscribe(() => {
      this.validateWorkTimeRangeDraft();
    });

    this.syncSatisfactionValidators();
    this.syncRobotGreetingControls();
    this.serviceNumberConfigForm.get('useGreetingQuestion')?.valueChanges.subscribe(() => this.syncRobotGreetingValidators());
    this.serviceNumberConfigForm.get('useHandOverTextReply')?.valueChanges.subscribe(() => this.syncRobotGreetingValidators());
    this.serviceNumberConfigForm.get('useRobot')?.valueChanges.subscribe(() => this.syncRobotGreetingControls());
    this.serviceNumberConfigForm.get('aiServiceStatus')?.valueChanges.subscribe(() => this.syncRobotGreetingControls());
    this.serviceNumberConfigForm.get('useRobotPriorityService')?.valueChanges.subscribe(() => this.syncRobotGreetingControls());
  }

  getWorkTimeRangeControl(index: number, controlName: string): AbstractControl | null {
    return this.workTimeRanges?.at(index)?.get(controlName) ?? null;
  }

  getEndMinuteOptions(index: number): Array<{ value: number; label: string }> {
    return this.getWorkTimeRangeControl(index, 'endHour')?.value === 24 ? [{ value: 0, label: '00' }] : this.workTimeMinuteOptions;
  }

  addWorkTimeRange(): void {
    this.workTimeRanges.push(this.createWorkTimeRangeGroup());
    this.validateWorkTimeRangeDraft();
  }

  removeWorkTimeRange(index: number): void {
    this.workTimeRanges.removeAt(index);
    this.validateWorkTimeRangeDraft();
  }

  openWorkTimeModal(): void {
    this.syncWorkTimeRangesFromValue(this.serviceNumberConfigForm.get('workTime')?.value);
    this.workTimeModalVisible = true;
  }

  closeWorkTimeModal(): void {
    this.workTimeModalVisible = false;
  }

  confirmWorkTimeModal(): void {
    const ranges = this.collectWorkTimeRanges();
    this.workTimeValidationError = ranges === null ? 'required' : validateWorkTimeRanges(ranges);
    if (this.workTimeValidationError) {
      this.markWorkTimeRangesAsDirty();
      return;
    }

    this.serviceNumberConfigForm.get('workTime')?.setValue(serializeWorkTimeRanges(ranges ?? []));
    this.workTimeModalVisible = false;
  }

  isWorkTimeModalConfirmDisabled(): boolean {
    return this.workTimeValidationError !== null || this.collectWorkTimeRanges() === null;
  }

  getWorkTimeValidationMessageKey(): string | null {
    switch (this.workTimeValidationError) {
      case 'required':
        return 'servicenumber.config.work-time-invalid-required';
      case 'format':
        return 'servicenumber.config.work-time-invalid-format';
      case 'order':
        return 'servicenumber.config.work-time-invalid-order';
      case 'overlap':
        return 'servicenumber.config.work-time-invalid-overlap';
      default:
        return null;
    }
  }

  private createWorkTimeRangeGroup(range?: WorkTimeRange): FormGroup {
    return this.fb.group({
      startHour: [range?.startHour ?? null],
      startMinute: [range?.startMinute ?? null],
      endHour: [range?.endHour ?? null],
      endMinute: [range?.endMinute ?? null]
    });
  }

  private formatTimeOption(value: number): string {
    return String(value).padStart(2, '0');
  }

  private syncWorkTimeRangesFromValue(value: unknown): void {
    const parsed = parseWorkTimeRanges(value);
    if (parsed === null) {
      this.workTimeValidationError = 'format';
      return;
    }

    this.workTimeValidationError = validateWorkTimeRanges(parsed);
    this.replaceWorkTimeRanges(parsed);
  }

  private replaceWorkTimeRanges(ranges: WorkTimeRange[]): void {
    if (!this.workTimeRanges) return;

    this.workTimeRanges.clear({ emitEvent: false });
    if (ranges.length === 0) {
      this.workTimeRanges.push(this.createWorkTimeRangeGroup(), { emitEvent: false });
      return;
    }
    ranges.forEach(range => this.workTimeRanges.push(this.createWorkTimeRangeGroup(range), { emitEvent: false }));
  }

  private collectWorkTimeRanges(): WorkTimeRange[] | null {
    if (!this.workTimeRanges || this.workTimeRanges.length === 0) return [];

    const ranges = this.workTimeRanges.controls.map(control => ({
      startHour: control.get('startHour')?.value,
      startMinute: control.get('startMinute')?.value,
      endHour: control.get('endHour')?.value,
      endMinute: control.get('endMinute')?.value
    }));

    return ranges.every(range => Object.values(range).every(value => Number.isInteger(value))) ? ranges : null;
  }

  private validateWorkTimeRangeDraft(): void {
    if (!this.workTimeRanges) return;

    this.workTimeRanges.controls.forEach(control => {
      if (control.get('endHour')?.value === 24 && control.get('endMinute')?.value !== 0) {
        control.get('endMinute')?.setValue(0, { emitEvent: false });
      }
    });

    const ranges = this.collectWorkTimeRanges();
    this.workTimeValidationError = ranges === null ? 'required' : validateWorkTimeRanges(ranges);
  }

  private markWorkTimeRangesAsDirty(): void {
    this.workTimeRanges?.controls.forEach(range => {
      Object.values(range.controls).forEach(control => {
        control.markAsDirty();
      });
    });
  }

  private syncSatisfactionValidators(): void {
    const useSurvey = !!this.serviceNumberConfigForm.get('useSatisfactionSurvey')?.value;
    const useNotice = !!this.serviceNumberConfigForm.get('useSatisfactionSurveyNotice')?.value;
    const noticeTypes = this.serviceNumberConfigForm.get('noticeType')?.value;
    const selectedNoticeTypes = Array.isArray(noticeTypes) ? noticeTypes : [];
    const surveyTypeCtrl = this.serviceNumberConfigForm.get('surveyType');
    const surveyRateCtrl = this.serviceNumberConfigForm.get('surveyRate');
    const noticeTypeCtrl = this.serviceNumberConfigForm.get('noticeType');
    const emailCtrl = this.serviceNumberConfigForm.get('noticeEmail');
    const smsCtrl = this.serviceNumberConfigForm.get('noticeSms');

    if (useSurvey) {
      surveyTypeCtrl?.setValidators([Validators.required]);
      surveyRateCtrl?.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
    } else {
      surveyTypeCtrl?.clearValidators();
      surveyRateCtrl?.clearValidators();
    }

    if (useSurvey && useNotice) {
      noticeTypeCtrl?.setValidators([Validators.required]);
      emailCtrl?.setValidators(selectedNoticeTypes.includes('Email') ? [Validators.required, Validators.email] : null);
      smsCtrl?.setValidators(selectedNoticeTypes.includes('Sms') ? [Validators.required] : null);
    } else {
      noticeTypeCtrl?.clearValidators();
      emailCtrl?.clearValidators();
      smsCtrl?.clearValidators();
    }

    surveyTypeCtrl?.updateValueAndValidity({ emitEvent: false });
    surveyRateCtrl?.updateValueAndValidity({ emitEvent: false });
    noticeTypeCtrl?.updateValueAndValidity({ emitEvent: false });
    emailCtrl?.updateValueAndValidity({ emitEvent: false });
    smsCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  // 仅允许选择未来日期：为空时通过；选择日期需晚于今天
  expireTimeFutureValidator = (control: AbstractControl) => {
    const val = control.value;
    if (!val) return null;
    const selected = new Date(val);
    const today = new Date();
    selected.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return selected.getTime() > today.getTime() ? null : { notFuture: true };
  };

  // 禁用今天及更早日期（配合日期选择器）
  disablePastDates = (current: Date): boolean => {
    if (!current) return false;
    const d = new Date(current);
    d.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d.getTime() <= today.getTime();
  };

  // 加载配置数据
  loadConfigData(): void {
    this.loading = true;
    this.http
      .post(`${ConstantUtil.ADMIN_SERVICENUMBER_CONFIG_DETAIL}/${this.serviceNumberId}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe((res: any) => {
        if (res.success && res.data) {
          const data = res.data;
          this.robotServiceType = data.robotServiceType ?? null;
          this.timeoutOptions = this.withCompatibleOption(this.baseTimeoutOptions, data.timeoutTime, 'minute');
          this.idleTimeOptions = this.withCompatibleOption(this.baseIdleTimeOptions, data.idleTime, 'second');
          const expireTs = data.expireTime as number | undefined;
          const parsedWorkTime = parseWorkTimeRanges(data.workTime);
          this.workTimeValidationError = parsedWorkTime === null ? 'format' : validateWorkTimeRanges(parsedWorkTime);
          this.replaceWorkTimeRanges(parsedWorkTime ?? []);

          let greetingText: string | null = data.greetingQuestionText ?? null;
          if (this.isGptRobotType(this.robotServiceType) && greetingText) {
            const greetingConfig = parseGreetingConfig(greetingText);
            greetingText = greetingConfig ? greetingConfig.message || null : greetingText;
          }

          this.serviceNumberConfigForm.patchValue(
            {
              ...data,
              robotServiceType: this.robotServiceType,
              greetingQuestionText: greetingText,
              workTime: parsedWorkTime && !this.workTimeValidationError ? serializeWorkTimeRanges(parsedWorkTime) : data.workTime,
              expireTime: expireTs && expireTs > 0 ? new Date(expireTs) : null
            },
            { emitEvent: false }
          );
          this.syncRobotGreetingControls();
          this.resetChangeDetection();
          this.cdr.detectChanges();
        }
      });
  }

  // 获取表单数据
  getFormData(): any {
    return this.serviceNumberConfigForm.getRawValue();
  }

  /** 判斷機器人是否已開通，與後端有效機器人判斷保持一致。 */
  isRobotEnabled(): boolean {
    return (
      this.serviceNumberConfigForm?.get('useRobot')?.value === true &&
      this.serviceNumberConfigForm?.get('aiServiceStatus')?.value === 'Active' &&
      this.serviceNumberConfigForm?.get('useRobotPriorityService')?.value === true
    );
  }

  /** 依機器人開通狀態控制可編輯欄位，停用時仍保留原值。 */
  private syncRobotGreetingControls(): void {
    if (!this.serviceNumberConfigForm) {
      return;
    }
    const robotEnabled = this.isRobotEnabled();
    ['firstGreetings', 'everyGreetings'].forEach(name => this.setControlEnabled(name, !robotEnabled));
    ['useGreetingQuestion', 'greetingQuestionText', 'useHandOverTextReply', 'handOverTextReplyText'].forEach(name =>
      this.setControlEnabled(name, robotEnabled)
    );
    this.syncRobotGreetingValidators();
  }

  private trimmedRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = String(control.value ?? '').trim();
      return value ? null : { required: true };
    };
  }

  getGreetingQuestionErrorKey(): string {
    const control = this.serviceNumberConfigForm?.get('greetingQuestionText');
    if (control?.hasError('maxlength')) {
      return 'servicenumber.robot.greeting-text-too-long';
    }
    return 'servicenumber.config.enter-ai-fixed-greeting';
  }

  private syncRobotGreetingValidators(): void {
    const greetingTextCtrl = this.serviceNumberConfigForm?.get('greetingQuestionText');
    const handoverTextCtrl = this.serviceNumberConfigForm?.get('handOverTextReplyText');
    if (!greetingTextCtrl || !handoverTextCtrl) {
      return;
    }

    const robotEnabled = this.isRobotEnabled();
    const useGreeting = robotEnabled && !!this.serviceNumberConfigForm.get('useGreetingQuestion')?.value;
    const useHandover = robotEnabled && !!this.serviceNumberConfigForm.get('useHandOverTextReply')?.value;

    if (useGreeting) {
      greetingTextCtrl.setValidators([
        Validators.required,
        Validators.maxLength(GREETING_CONFIG_MAX_LENGTH),
        this.trimmedRequiredValidator()
      ]);
    } else {
      greetingTextCtrl.setValidators([Validators.maxLength(GREETING_CONFIG_MAX_LENGTH)]);
      if (greetingTextCtrl.hasError('required')) {
        greetingTextCtrl.setErrors(null);
      }
    }

    if (useHandover) {
      handoverTextCtrl.setValidators([Validators.required, this.trimmedRequiredValidator()]);
    } else {
      handoverTextCtrl.clearValidators();
      if (handoverTextCtrl.hasError('required')) {
        handoverTextCtrl.setErrors(null);
      }
    }

    greetingTextCtrl.updateValueAndValidity({ emitEvent: false });
    handoverTextCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private setControlEnabled(name: string, enabled: boolean): void {
    const control = this.serviceNumberConfigForm.get(name);
    if (!control || control.enabled === enabled) {
      return;
    }
    if (enabled) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }

  normalizeGreetingQuestionForSave(value: unknown): string | null {
    const message = String(value ?? '').trim();
    if (!message) {
      return null;
    }
    return this.isGptRobotType(this.robotServiceType) ? stringifyGreetingConfig(message) : message;
  }

  isGptRobotType(type: unknown): boolean {
    return (
      String(type ?? '')
        .trim()
        .toLowerCase() === 'gpt'
    );
  }

  // 新增方法：判断是否为Boss类型
  isBossType(): boolean {
    return this.serviceNumberType === 'Boss';
  }

  // 新增：仅一般租户(Common)或官方租户(Official)
  private isAllowedTenantType(): boolean {
    return this.tenantType === 'Common' || this.tenantType === 'Official';
  }

  // 新增：服务号类型需非 Official/Manage
  private isNonOfficialNonManageType(): boolean {
    return this.serviceNumberType !== 'Official' && this.serviceNumberType !== 'Manage';
  }

  // 新增：expireTime 可见性判断
  canShowExpireTime(): boolean {
    return this.isAllowedTenantType() && this.isNonOfficialNonManageType();
  }

  getOptionLabel(option: ServiceConfigOption): string {
    if (option.labelKey) {
      return this.i18n.fanyi(option.labelKey);
    }
    return option.labelText || String(option.value);
  }

  private withCompatibleOption(baseOptions: ServiceConfigOption[], rawValue: unknown, unit: 'minute' | 'second'): ServiceConfigOption[] {
    const options = [...baseOptions];
    const value = Number(rawValue);
    if (!Number.isFinite(value) || value <= 0) {
      return options;
    }
    if (options.some(option => option.value === value)) {
      return options;
    }
    return [
      {
        value,
        labelText: this.buildCompatibleLabel(value, unit),
        transient: true
      },
      ...options
    ];
  }

  private buildCompatibleLabel(value: number, unit: 'minute' | 'second'): string {
    const normalizedMinutes = unit === 'second' ? value / 60 : value;
    if (normalizedMinutes >= 60 && normalizedMinutes % 60 === 0) {
      return this.interpolateCountLabel('servicenumber.config.compat-option.hours', normalizedMinutes / 60);
    }
    return this.interpolateCountLabel('servicenumber.config.compat-option.minutes', normalizedMinutes);
  }

  private interpolateCountLabel(key: string, count: number): string {
    const tpl = this.i18n.fanyi(key) || '';
    return tpl.replace('{count}', String(count));
  }

  saveServiceConfig(): Promise<boolean> {
    const workTimeRanges = parseWorkTimeRanges(this.serviceNumberConfigForm.get('workTime')?.value);
    this.workTimeValidationError = workTimeRanges === null ? 'format' : validateWorkTimeRanges(workTimeRanges);
    if (this.workTimeValidationError) {
      Object.values(this.serviceNumberConfigForm.controls).forEach(control => {
        control.markAsDirty();
      });
      this.markWorkTimeRangesAsDirty();
      this.message.error(this.i18n.fanyi(this.getWorkTimeValidationMessageKey() ?? 'servicenumber.config.required-fields-incomplete'));
      return Promise.resolve(false);
    }

    const normalizedWorkTime = serializeWorkTimeRanges(workTimeRanges ?? []);
    this.serviceNumberConfigForm.get('workTime')?.setValue(normalizedWorkTime, { emitEvent: false });
    this.syncSatisfactionValidators();
    this.syncRobotGreetingValidators();
    // 根据 useVoiceToText 的值决定是否验证 voiceToTextJson
    const useVoiceToText = this.serviceNumberConfigForm.get('useVoiceToText')?.value;
    if (!useVoiceToText) {
      // 如果未启用语音转文本，则移除 voiceToTextJson 的验证错误
      this.serviceNumberConfigForm.get('voiceToTextJson')?.setErrors(null);
    }

    if (this.serviceNumberConfigForm.invalid) {
      // 标记所有表单控件为已触摸，以显示验证错误
      Object.values(this.serviceNumberConfigForm.controls).forEach(control => {
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity();
      });
      this.message.error(this.i18n.fanyi('servicenumber.config.required-fields-incomplete'));
      return Promise.resolve(false);
    }

    this.isSaving = true;
    const formValue = this.serviceNumberConfigForm.getRawValue() as any;
    const payload = {
      ...formValue,
      greetingQuestionText: this.normalizeGreetingQuestionForSave(formValue.greetingQuestionText),
      expireTime: formValue.expireTime ? new Date(formValue.expireTime).getTime() : 0
    };

    // 调用保存API
    return new Promise(resolve => {
      this.http
        .post(ConstantUtil.ADMIN_SERVICENUMBER_CONFIG_SAVE, payload)
        .pipe(
          finalize(() => {
            this.isSaving = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe({
          next: (res: any) => {
            if (res.success) {
              this.message.success(this.i18n.fanyi('servicenumber.config.save-success'));
              this.resetChangeDetection();
              resolve(true);
            } else {
              this.message.error(res.msg ?? this.i18n.fanyi('servicenumber.config.save-failed'));
              resolve(false);
            }
          },
          error: () => {
            this.message.error(this.i18n.fanyi('servicenumber.config.save-failed-retry'));
            resolve(false);
          }
        });
    });
  }

  hasPendingChanges(): boolean {
    this.hasUnsavedChanges = this.formChangeTracker?.hasChanged() ?? false;
    this.unsavedChangesChange.emit(this.hasUnsavedChanges);
    return this.hasUnsavedChanges;
  }

  canDeactivate(): Promise<boolean> {
    if (!this.hasPendingChanges()) {
      return Promise.resolve(true);
    }

    return new Promise(resolve => {
      this.modal.confirm({
        nzTitle: this.i18n.fanyi('servicenumber.config.unsaved-modal-title'),
        nzContent: this.i18n.fanyi('servicenumber.config.unsaved-modal-content'),
        nzOkText: this.i18n.fanyi('servicenumber.config.save-and-leave'),
        nzCancelText: this.i18n.fanyi('servicenumber.config.leave-without-saving'),
        nzIconType: 'exclamation-circle',
        nzClosable: false,
        nzOnOk: () =>
          this.saveServiceConfig().then(saved => {
            resolve(saved);
          }),
        nzOnCancel: () => {
          this.resetChangeDetection();
          resolve(true);
        }
      });
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (this.hasPendingChanges()) {
      $event.returnValue = true;
    }
  }

  private setupFormChangeDetection(): void {
    this.formChangeTracker?.stop();
    this.formChangeTracker = new FormChangeTracker(this.serviceNumberConfigForm, () => this.serviceNumberConfigForm.getRawValue());
    this.formChangeTracker.start(hasChanged => {
      this.hasUnsavedChanges = hasChanged;
      this.unsavedChangesChange.emit(hasChanged);
      this.cdr.detectChanges();
    });
  }

  private resetChangeDetection(): void {
    this.formChangeTracker?.reset();
    this.hasUnsavedChanges = false;
    this.unsavedChangesChange.emit(false);
  }
}
